import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { z } from "zod";
import { toast } from "sonner";
import { axiosPrivate } from "@/api/axios";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const notificationFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  body: z.string().min(1, "Body is required"),
  type: z.string().min(1, "Type is required"),
  broadcast_type: z.enum(["per_user", "everyone", "all_users", "all_experts"], {
    required_error: "Broadcast type is required",
  }),
  target_user_id: z.coerce.number().optional(),
  sender_id: z.coerce.number().optional(),
});

function AddNotification() {
  const navigate = useNavigate();
  
  const form = useForm({
    resolver: zodResolver(notificationFormSchema),
    defaultValues: {
      title: "",
      body: "",
      type: "",
      broadcast_type: "per_user" as const,
      target_user_id: 0,
      sender_id: 0,
    },
  });

  const broadcastType = form.watch("broadcast_type");

  const notificationCreateMutation = useMutation({
    mutationFn: async (data: any) => {
      // Use broadcast API for everyone, all_users, or all_experts
      if (data.broadcast_type === "everyone" || data.broadcast_type === "all_users" || data.broadcast_type === "all_experts") {
        const broadcastPayload: any = {
          title: data.title,
          body: data.body,
        };
        
        // Only add role_filter if not "everyone"
        if (data.broadcast_type !== "everyone") {
          broadcastPayload.role_filter = data.broadcast_type === "all_users" ? "user" : "expert";
        }
        
        const response = await axiosPrivate.post("/notifications/broadcast", broadcastPayload);
        return response.data;
      } else {
        // Use regular notification API for per_user
        const payload = {
          title: data.title,
          body: data.body,
          type: data.type,
          target_user_id: data.target_user_id || 0,
          sender_id: data.sender_id || 0,
        };
        const response = await axiosPrivate.post("/notifications/", payload);
        return response.data;
      }
    },
    onSuccess: () => {
      form.reset();
      toast.success("Notification sent successfully");
      navigate("/dashboard/notifications/manage-notification");
    },
    onError: (error: any) => {
      toast.error("Failed to send notification");
    },
  });

  async function onSubmit(data: z.infer<typeof notificationFormSchema>) {
    notificationCreateMutation.mutate(data);
  }

  return (
    <div className="space-y-4 sm:space-y-8">
      <h1 className="text-2xl">Create Notification</h1>
      <Form {...form}>
        <form className="space-y-4 sm:space-y-8" onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Title</FormLabel>
                <FormControl>
                  <Input placeholder="Enter Notification Title" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="body"
            render={({ field }) => (
              <FormItem className="space-y-1">
                <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Body</FormLabel>
                <FormControl>
                  <Textarea placeholder="Enter Notification Body" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Type</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Notification Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="info">Info</SelectItem>
                        <SelectItem value="warning">Warning</SelectItem>
                        <SelectItem value="success">Success</SelectItem>
                        <SelectItem value="error">Error</SelectItem>
                        <SelectItem value="promotional">Promotional</SelectItem>
                        <SelectItem value="reminder">Reminder</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="broadcast_type"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Broadcast To</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Broadcast Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per_user">Per User</SelectItem>
                        <SelectItem value="everyone">Everyone</SelectItem>
                        <SelectItem value="all_users">All Users</SelectItem>
                        <SelectItem value="all_experts">All Experts</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormDescription>
                    Choose who should receive this notification
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {broadcastType === "per_user" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="target_user_id"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Target User ID (optional)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="Enter target user ID (0 for all users)" 
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription>
                      Leave as 0 to send to all users, or specify a user ID for targeted notifications
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="sender_id"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Sender ID (optional)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="Enter sender ID" 
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription>
                      ID of the user sending the notification
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
          <Button
            type="submit"
            disabled={
              notificationCreateMutation.isPending ||
              !form.formState.isValid ||
              notificationCreateMutation.isSuccess
            }
          >
            {notificationCreateMutation.isPending ? "Sending..." : "Send Notification"}
          </Button>
        </form>
      </Form>
    </div>
  );
}

export default AddNotification;
